import Dexie, { type Table } from 'dexie';
import type {
  ModuleLayoutState,
  SavedItem,
  Project,
  Task,
  FeedbackSignal,
  PreferenceWeight,
  Preset,
  ApiKeyEntry,
  ServiceStatusEntry,
  FeedItem,
  SourceVaultEntry,
  FeedActivity,
  FeedCacheRecord,
  CalendarEvent,
  NoteEntry,
  Bookmark,
  SavedSearch,
  FocusSession,
  ReadingQueueItem,
  CustomOrbitRecord,
  CustomOrbitSettings,
} from '@/types';
import { localDateKey } from '@/utils/datetime';

export class PhexotialDB extends Dexie {
  layouts!: Table<ModuleLayoutState, string>;
  savedItems!: Table<SavedItem, string>;
  projects!: Table<Project, string>;
  tasks!: Table<Task, string>;
  feedback!: Table<FeedbackSignal, string>;
  preferences!: Table<PreferenceWeight, string>;
  presets!: Table<Preset, string>;
  apiKeys!: Table<ApiKeyEntry, string>;
  serviceStatus!: Table<ServiceStatusEntry, string>;
  feedCache!: Table<FeedCacheRecord, string>;
  feedItems!: Table<FeedItem, string>;
  sourceVault!: Table<SourceVaultEntry, string>;
  feedActivity!: Table<FeedActivity, string>;
  calendarEvents!: Table<CalendarEvent, string>;
  notes!: Table<NoteEntry, string>;
  bookmarks!: Table<Bookmark, string>;
  savedSearches!: Table<SavedSearch, string>;
  focusSessions!: Table<FocusSession, string>;
  readingQueue!: Table<ReadingQueueItem, string>;
  customOrbit!: Table<CustomOrbitRecord, string>;
  appState!: Table<{ key: string; value: unknown }>;

  constructor() {
    super('InternetObservatory');
    this.version(1).stores({
      layouts: 'id, type, hidden, zIndex',
      savedItems: 'id, sourceItemId, provider, savedAt, *folders, *tags, *projectIds',
      projects: 'id, name, status, createdAt',
      tasks: 'id, projectId, completed, order, createdAt',
      feedback: '++id, itemId, providerId, action, timestamp',
      preferences: 'key, type, updatedAt',
      layoutSnapshots: 'id, name, viewportClass, createdAt',
      apiKeys: 'providerId',
      serviceStatus: 'id, name',
      feedCache: 'key, cachedAt',
    });
    this.version(2).stores({
      feedItems: 'id, providerId, sourceName, *tags, fetchedAt, publishedAt',
    });
    this.version(3).stores({
      sourceVault: 'id, type, enabled, createdAt, updatedAt',
    });
    this.version(4).stores({
      feedActivity: 'date',
    });
    this.version(5).stores({
      presets: 'id, name, builtIn, createdAt, updatedAt',
      layoutSnapshots: null,
    });
    this.version(6).stores({
      feedCache: 'key, cachedAt, providerId',
    }).upgrade(async (tx) => {
      await tx.table('feedCache').clear();
    });
    this.version(7).stores({
      calendarEvents: 'id, date, source, sourceId',
      notes: 'id, date, pinned, *tags, updatedAt',
      bookmarks: 'id, url, pinned, *folders, *tags, createdAt',
      savedSearches: 'id, name, createdAt',
    });
    this.version(8).stores({
      appState: 'key',
    });
    this.version(9).stores({
      focusSessions: 'id, startTime, mode, completed',
      readingQueue: 'id, itemId, status, position, addedAt',
    });
    this.version(10).stores({
      customOrbit: 'id',
    });
  }
}

export const db = new PhexotialDB();

export async function initDB(): Promise<void> {
  await db.open();
}

// --- Layout persistence ---
export async function saveLayouts(modules: ModuleLayoutState[]): Promise<void> {
  await db.transaction('rw', db.layouts, async () => {
    await db.layouts.clear();
    await db.layouts.bulkPut(modules);
  });
}

export async function loadLayouts(): Promise<ModuleLayoutState[]> {
  return db.layouts.toArray();
}

// --- Saved items ---
export async function saveSavedItem(item: SavedItem): Promise<void> {
  await db.savedItems.put(item);
}

export async function removeSavedItem(id: string): Promise<void> {
  await db.savedItems.delete(id);
}

export async function getSavedItems(): Promise<SavedItem[]> {
  return db.savedItems.orderBy('savedAt').reverse().toArray();
}

// --- App state persistence (feedItemStates, muteSettings, cardViews) ---
export async function saveAppState(key: string, value: unknown): Promise<void> {
  await db.appState.put({ key, value });
}

export async function getAppState<T>(key: string): Promise<T | undefined> {
  const entry = await db.appState.get(key);
  return entry?.value as T | undefined;
}

export async function isSaved(sourceItemId: string): Promise<boolean> {
  const count = await db.savedItems.where('sourceItemId').equals(sourceItemId).count();
  return count > 0;
}

// --- Projects ---
export async function saveProject(project: Project): Promise<void> {
  await db.projects.put(project);
}

export async function getProjects(): Promise<Project[]> {
  return db.projects.toArray();
}

export async function deleteProject(id: string): Promise<void> {
  await db.projects.delete(id);
  await db.tasks.where('projectId').equals(id).delete();
}

// --- Tasks ---
export async function saveTask(task: Task): Promise<void> {
  await db.tasks.put(task);
}

export async function getTasks(): Promise<Task[]> {
  return db.tasks.orderBy('order').toArray();
}

export async function deleteTask(id: string): Promise<void> {
  await db.tasks.delete(id);
}

// --- Feedback ---
export async function recordFeedback(signal: FeedbackSignal): Promise<void> {
  await db.feedback.add(signal);
}

export async function getFeedback(): Promise<FeedbackSignal[]> {
  return db.feedback.toArray();
}

// --- Preferences ---
export async function savePreference(weight: PreferenceWeight): Promise<void> {
  await db.preferences.put(weight);
}

export async function bulkPutPreferences(weights: PreferenceWeight[]): Promise<void> {
  await db.preferences.bulkPut(weights);
}

export async function getPreferences(): Promise<PreferenceWeight[]> {
  return db.preferences.toArray();
}

export async function clearPreferences(): Promise<void> {
  await db.preferences.clear();
  await db.feedback.clear();
}

// --- Presets ---
export async function savePreset(preset: Preset): Promise<void> {
  await db.presets.put(preset);
}

export async function getPresets(): Promise<Preset[]> {
  return db.presets.toArray();
}

export async function deletePreset(id: string): Promise<void> {
  await db.presets.delete(id);
}

// --- API Keys ---
export async function saveApiKey(entry: ApiKeyEntry & { key: string }): Promise<void> {
  await db.apiKeys.put(entry);
}

export async function getApiKey(providerId: string): Promise<(ApiKeyEntry & { key: string }) | undefined> {
  return db.apiKeys.get(providerId) as Promise<(ApiKeyEntry & { key: string }) | undefined>;
}

export async function deleteApiKey(providerId: string): Promise<void> {
  await db.apiKeys.delete(providerId);
}

// --- Service Status ---
export async function saveServiceStatus(entry: ServiceStatusEntry): Promise<void> {
  await db.serviceStatus.put(entry);
}

export async function getServiceStatusEntries(): Promise<ServiceStatusEntry[]> {
  return db.serviceStatus.toArray();
}

// --- Feed cache ---
export async function setFeedCache(record: FeedCacheRecord): Promise<void> {
  await db.feedCache.put(record);
}

export async function getFeedCache(key: string): Promise<FeedCacheRecord | undefined> {
  return db.feedCache.get(key);
}

export function isValidFeedCacheRecord(record: unknown): record is FeedCacheRecord {
  if (typeof record !== 'object' || record === null) return false;
  const r = record as Record<string, unknown>;
  return typeof r.key === 'string' &&
    'raw' in r &&
    typeof r.cachedAt === 'string' &&
    typeof r.hasMore === 'boolean';
}

export async function clearFeedCache(): Promise<void> {
  await db.feedCache.clear();
}

// --- Feed Items (search index) ---
export async function bulkInsertFeedItems(items: FeedItem[]): Promise<void> {
  await db.feedItems.bulkPut(items);
}

export async function getAllFeedItems(): Promise<FeedItem[]> {
  return db.feedItems.toArray();
}

export async function getFeedItemsByProvider(providerId: string): Promise<FeedItem[]> {
  return db.feedItems.where('providerId').equals(providerId).toArray();
}

export async function clearFeedItems(): Promise<void> {
  await db.feedItems.clear();
}

// --- Export/Import ---
export const BACKUP_VERSION = 10;

export async function exportAllData(): Promise<Record<string, unknown>> {
  const [layouts, savedItems, projects, tasks, feedback, preferences, presets, serviceStatus, feedItems, sourceVault, feedActivity, calendarEvents, notes, bookmarks, savedSearches, focusSessions, readingQueue, appState, customOrbitMeta] =
    await Promise.all([
      db.layouts.toArray(),
      db.savedItems.toArray(),
      db.projects.toArray(),
      db.tasks.toArray(),
      db.feedback.toArray(),
      db.preferences.toArray(),
      db.presets.toArray(),
      db.serviceStatus.toArray(),
      db.feedItems.toArray(),
      db.sourceVault.toArray(),
      db.feedActivity.toArray(),
      db.calendarEvents.toArray(),
      db.notes.toArray(),
      db.bookmarks.toArray(),
      db.savedSearches.toArray(),
      db.focusSessions.toArray(),
      db.readingQueue.toArray(),
      db.appState.toArray(),
      db.customOrbit.toArray().then((records) =>
        records.map((r) => ({
          id: r.id,
          fileName: r.fileName,
          mimeType: r.mimeType,
          sizeBytes: r.sizeBytes,
          durationSeconds: r.durationSeconds,
          width: r.width,
          height: r.height,
          hasPoster: r.posterBlob !== null,
          importedAt: r.importedAt,
          updatedAt: r.updatedAt,
        }))
      ),
    ]);

  // Custom Orbit settings from appState (not the blob)
  const customOrbitSettings = await getAppState<CustomOrbitSettings>('customOrbitSettings');

  return {
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    layouts,
    savedItems,
    projects,
    tasks,
    feedback,
    preferences,
    presets,
    serviceStatus,
    feedItems,
    sourceVault,
    feedActivity,
    calendarEvents,
    notes,
    bookmarks,
    savedSearches,
    focusSessions,
    readingQueue,
    appState,
    // Custom Orbit: settings metadata only — blobs excluded from backup
    customOrbit: customOrbitMeta,
    customOrbitSettings: customOrbitSettings ?? null,
  };
}

const TABLE_NAMES = [
  'layouts', 'savedItems', 'projects', 'tasks', 'feedback',
  'preferences', 'presets', 'serviceStatus', 'feedItems', 'sourceVault', 'feedActivity',
  'calendarEvents', 'notes', 'bookmarks', 'savedSearches', 'focusSessions', 'readingQueue', 'appState',
  'customOrbit',
] as const;

const REQUIRED_ID_FIELDS: Record<string, string> = {
  layouts: 'id',
  savedItems: 'id',
  projects: 'id',
  tasks: 'id',
  feedback: 'id',
  preferences: 'id',
  presets: 'id',
  serviceStatus: 'id',
  feedItems: 'id',
  sourceVault: 'id',
  feedActivity: 'date',
  calendarEvents: 'id',
  notes: 'id',
  bookmarks: 'id',
  savedSearches: 'id',
  focusSessions: 'id',
  readingQueue: 'id',
  appState: 'key',
  customOrbit: 'id',
};

export function validateBackup(data: Record<string, unknown>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Backup data is not an object'] };
  }

  const version = data.version;
  if (typeof version !== 'number' || version < 1 || !Number.isFinite(version)) {
    errors.push('Missing or invalid backup version');
  }

  if (typeof version === 'number' && version > BACKUP_VERSION) {
    errors.push(`Backup version ${version} is newer than supported version ${BACKUP_VERSION}`);
  }

  for (const key of TABLE_NAMES) {
    if (key in data) {
      if (!Array.isArray(data[key])) {
        errors.push(`Field "${key}" is not an array`);
        continue;
      }

      const idField = REQUIRED_ID_FIELDS[key];
      const records = data[key] as unknown[];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        if (!record || typeof record !== 'object') {
          errors.push(`"${key}"[${i}] is not an object`);
          continue;
        }
        const rec = record as Record<string, unknown>;
        if (idField && (rec[idField] === undefined || rec[idField] === null || rec[idField] === '')) {
          errors.push(`"${key}"[${i}] missing required field "${idField}"`);
        }
        for (const [field, value] of Object.entries(rec)) {
          if (typeof value === 'number' && !Number.isFinite(value)) {
            errors.push(`"${key}"[${i}].${field} is ${String(value)}`);
          }
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

export async function importData(data: Record<string, unknown>): Promise<void> {
  const validation = validateBackup(data);
  if (!validation.valid) {
    throw new Error('Invalid backup data: ' + validation.errors.join(', '));
  }

  // Use a transaction for atomicity across all tables
  await db.transaction('rw', [db.layouts, db.savedItems, db.projects, db.tasks, db.feedback, db.preferences, db.feedItems, db.sourceVault, db.presets, db.serviceStatus, db.feedActivity, db.calendarEvents, db.notes, db.bookmarks, db.savedSearches, db.focusSessions, db.readingQueue, db.appState], async () => {
    if (data.layouts && Array.isArray(data.layouts)) {
      await db.layouts.clear();
      await db.layouts.bulkPut(data.layouts as ModuleLayoutState[]);
    }
    if (data.savedItems && Array.isArray(data.savedItems)) {
      await db.savedItems.clear();
      await db.savedItems.bulkPut(data.savedItems as SavedItem[]);
    }
    if (data.projects && Array.isArray(data.projects)) {
      await db.projects.clear();
      await db.projects.bulkPut(data.projects as Project[]);
    }
    if (data.tasks && Array.isArray(data.tasks)) {
      await db.tasks.clear();
      await db.tasks.bulkPut(data.tasks as Task[]);
    }
    if (data.feedback && Array.isArray(data.feedback)) {
      await db.feedback.clear();
      await db.feedback.bulkAdd(data.feedback as FeedbackSignal[]);
    }
    if (data.preferences && Array.isArray(data.preferences)) {
      await db.preferences.clear();
      await db.preferences.bulkPut(data.preferences as PreferenceWeight[]);
    }
    if (data.feedItems && Array.isArray(data.feedItems)) {
      await db.feedItems.clear();
      await db.feedItems.bulkPut(data.feedItems as FeedItem[]);
    }
    if (data.sourceVault && Array.isArray(data.sourceVault)) {
      await db.sourceVault.clear();
      await db.sourceVault.bulkPut(data.sourceVault as SourceVaultEntry[]);
    }
    if (data.presets && Array.isArray(data.presets)) {
      await db.presets.clear();
      await db.presets.bulkPut(data.presets as Preset[]);
    }
    if (data.serviceStatus && Array.isArray(data.serviceStatus)) {
      await db.serviceStatus.clear();
      await db.serviceStatus.bulkPut(data.serviceStatus as ServiceStatusEntry[]);
    }
    if (data.feedActivity && Array.isArray(data.feedActivity)) {
      await db.feedActivity.clear();
      await db.feedActivity.bulkPut(data.feedActivity as FeedActivity[]);
    }
    if (data.calendarEvents && Array.isArray(data.calendarEvents)) {
      await db.calendarEvents.clear();
      await db.calendarEvents.bulkPut(data.calendarEvents as CalendarEvent[]);
    }
    if (data.notes && Array.isArray(data.notes)) {
      await db.notes.clear();
      await db.notes.bulkPut(data.notes as NoteEntry[]);
    }
    if (data.bookmarks && Array.isArray(data.bookmarks)) {
      await db.bookmarks.clear();
      await db.bookmarks.bulkPut(data.bookmarks as Bookmark[]);
    }
    if (data.savedSearches && Array.isArray(data.savedSearches)) {
      await db.savedSearches.clear();
      await db.savedSearches.bulkPut(data.savedSearches as SavedSearch[]);
    }
    if (data.focusSessions && Array.isArray(data.focusSessions)) {
      await db.focusSessions.clear();
      await db.focusSessions.bulkPut(data.focusSessions as FocusSession[]);
    }
    if (data.readingQueue && Array.isArray(data.readingQueue)) {
      await db.readingQueue.clear();
      await db.readingQueue.bulkPut(data.readingQueue as ReadingQueueItem[]);
    }
    if (data.appState && Array.isArray(data.appState)) {
      await db.appState.clear();
      await db.appState.bulkPut(data.appState as { key: string; value: unknown }[]);
    }
    // Custom Orbit: only restore settings metadata, never blobs
    if (data.customOrbitSettings && typeof data.customOrbitSettings === 'object') {
      await db.appState.put({ key: 'customOrbitSettings', value: data.customOrbitSettings });
    }
  });
}

export async function resetAllData(): Promise<void> {
  await db.transaction('rw', [
    db.layouts, db.savedItems, db.projects, db.tasks, db.feedback,
    db.preferences, db.presets, db.apiKeys, db.serviceStatus,
    db.feedCache, db.feedItems, db.sourceVault, db.feedActivity,
    db.calendarEvents, db.notes, db.bookmarks, db.savedSearches,
    db.focusSessions, db.readingQueue, db.appState, db.customOrbit,
  ], async () => {
    await Promise.all([
      db.layouts.clear(),
      db.savedItems.clear(),
      db.projects.clear(),
      db.tasks.clear(),
      db.feedback.clear(),
      db.preferences.clear(),
      db.presets.clear(),
      db.apiKeys.clear(),
      db.serviceStatus.clear(),
      db.feedCache.clear(),
      db.feedItems.clear(),
      db.sourceVault.clear(),
      db.feedActivity.clear(),
      db.calendarEvents.clear(),
      db.notes.clear(),
      db.bookmarks.clear(),
      db.savedSearches.clear(),
      db.focusSessions.clear(),
      db.readingQueue.clear(),
      db.appState.clear(),
      db.customOrbit.clear(),
    ]);
  });
}

export async function resetLayoutData(): Promise<void> {
  await db.transaction('rw', [db.layouts], async () => {
    await db.layouts.clear();
  });
}

// --- Custom Orbit storage ---

export async function saveCustomOrbitRecord(record: CustomOrbitRecord): Promise<void> {
  await db.customOrbit.put(record);
}

export async function getCustomOrbitRecord(): Promise<CustomOrbitRecord | undefined> {
  return db.customOrbit.get('custom-orbit');
}

export async function deleteCustomOrbitRecord(): Promise<void> {
  await db.customOrbit.delete('custom-orbit');
}

export async function getCustomOrbitSettings(): Promise<CustomOrbitSettings | undefined> {
  return getAppState<CustomOrbitSettings>('customOrbitSettings');
}

export async function saveCustomOrbitSettings(settings: CustomOrbitSettings): Promise<void> {
  await saveAppState('customOrbitSettings', settings);
}

export function isValidCustomOrbitRecord(record: unknown): record is CustomOrbitRecord {
  if (typeof record !== 'object' || record === null) return false;
  const r = record as Record<string, unknown>;
  return r.id === 'custom-orbit' &&
    r.blob instanceof Blob &&
    typeof r.fileName === 'string' &&
    typeof r.mimeType === 'string' &&
    typeof r.sizeBytes === 'number' && Number.isFinite(r.sizeBytes) &&
    typeof r.durationSeconds === 'number' && Number.isFinite(r.durationSeconds) &&
    typeof r.width === 'number' && Number.isFinite(r.width) &&
    typeof r.height === 'number' && Number.isFinite(r.height) &&
    (r.posterBlob === null || r.posterBlob instanceof Blob) &&
    typeof r.importedAt === 'string' &&
    typeof r.updatedAt === 'string';
}

export async function resetFeedData(): Promise<void> {
  await Promise.all([
    db.feedCache.clear(),
    db.feedItems.clear(),
    db.feedback.clear(),
    db.preferences.clear(),
  ]);
}

export async function resetProjectData(): Promise<void> {
  await Promise.all([
    db.projects.clear(),
    db.tasks.clear(),
  ]);
}

export async function resetPersonalizationData(): Promise<void> {
  await Promise.all([
    db.feedback.clear(),
    db.preferences.clear(),
  ]);
}

// --- Source Vault ---
export async function saveSourceVaultEntry(entry: SourceVaultEntry): Promise<void> {
  await db.sourceVault.put(entry);
}

export async function getSourceVaultEntries(): Promise<SourceVaultEntry[]> {
  return db.sourceVault.orderBy('createdAt').toArray();
}

export async function deleteSourceVaultEntry(id: string): Promise<void> {
  await db.sourceVault.delete(id);
}

// --- Feed Activity (streak tracking) ---
function todayKey(): string {
  return localDateKey();
}

export async function recordFeedActivity(field: 'itemsViewed' | 'itemsOpened' | 'itemsSaved', count: number = 1): Promise<void> {
  const date = todayKey();
  const existing = await db.feedActivity.get(date);
  if (existing) {
    await db.feedActivity.put({
      ...existing,
      [field]: (existing[field] ?? 0) + count,
    });
  } else {
    await db.feedActivity.put({
      date,
      itemsViewed: field === 'itemsViewed' ? count : 0,
      itemsOpened: field === 'itemsOpened' ? count : 0,
      itemsSaved: field === 'itemsSaved' ? count : 0,
    });
  }
}

export async function getFeedActivity(days: number = 30): Promise<FeedActivity[]> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = localDateKey(cutoff);
  return db.feedActivity.where('date').aboveOrEqual(cutoffStr).toArray();
}

export async function getFeedStreak(): Promise<number> {
  const all = await db.feedActivity.toArray();
  const activeDays = new Set(all.filter((a) => a.itemsOpened > 0).map((a) => a.date));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = localDateKey(d);
    if (activeDays.has(key)) {
      streak++;
    } else if (i === 0) {
      continue;
    } else {
      break;
    }
  }
  return streak;
}

export async function clearSourceVault(): Promise<void> {
  await db.sourceVault.clear();
}

// --- Calendar Events ---
export async function saveCalendarEvent(event: CalendarEvent): Promise<void> {
  await db.calendarEvents.put(event);
}

export async function deleteCalendarEvent(id: string): Promise<void> {
  await db.calendarEvents.delete(id);
}

export async function getCalendarEvents(): Promise<CalendarEvent[]> {
  return db.calendarEvents.orderBy('date').toArray();
}

export async function getCalendarEventsForMonth(year: number, month: number): Promise<CalendarEvent[]> {
  const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
  return db.calendarEvents.where('date').startsWith(prefix).toArray();
}

// --- Notes ---
export async function saveNote(note: NoteEntry): Promise<void> {
  await db.notes.put(note);
}

export async function deleteNote(id: string): Promise<void> {
  await db.notes.delete(id);
}

export async function getNotes(): Promise<NoteEntry[]> {
  return db.notes.orderBy('updatedAt').reverse().toArray();
}

export async function getNoteById(id: string): Promise<NoteEntry | undefined> {
  return db.notes.get(id);
}

// --- Bookmarks ---
export async function saveBookmark(bookmark: Bookmark): Promise<void> {
  await db.bookmarks.put(bookmark);
}

export async function deleteBookmark(id: string): Promise<void> {
  await db.bookmarks.delete(id);
}

export async function getBookmarks(): Promise<Bookmark[]> {
  return db.bookmarks.orderBy('createdAt').reverse().toArray();
}

// --- Saved Searches ---
export async function saveSavedSearch(search: SavedSearch): Promise<void> {
  await db.savedSearches.put(search);
}

export async function deleteSavedSearch(id: string): Promise<void> {
  await db.savedSearches.delete(id);
}

export async function getSavedSearches(): Promise<SavedSearch[]> {
  return db.savedSearches.orderBy('createdAt').toArray();
}

// --- Feed storage retention/cleanup ---
export async function cleanupFeedItems(maxAgeDays = 30, maxCount = 5000): Promise<void> {
  const cutoff = new Date(Date.now() - maxAgeDays * 24 * 60 * 60 * 1000).toISOString();
  await db.feedItems.where('fetchedAt').below(cutoff).delete();
  const remaining = await db.feedItems.count();
  if (remaining > maxCount) {
    const oldest = await db.feedItems.orderBy('fetchedAt').limit(remaining - maxCount).toArray();
    await db.feedItems.bulkDelete(oldest.map((item) => item.id));
  }
}

export async function cleanupFeedCache(maxAgeHours = 24): Promise<void> {
  const cutoff = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000).toISOString();
  await db.feedCache.where('cachedAt').below(cutoff).delete();
}

// --- Focus Sessions ---
export async function saveFocusSession(session: FocusSession): Promise<void> {
  await db.focusSessions.put(session);
}

export async function getFocusSessions(): Promise<FocusSession[]> {
  return db.focusSessions.orderBy('startTime').reverse().toArray();
}

export async function deleteFocusSession(id: string): Promise<void> {
  await db.focusSessions.delete(id);
}

export async function getRecentFocusSessions(limit: number = 10): Promise<FocusSession[]> {
  const all = await db.focusSessions.orderBy('startTime').reverse().toArray();
  return all.slice(0, limit);
}

// --- Reading Queue ---
export async function saveReadingQueueItem(item: ReadingQueueItem): Promise<void> {
  await db.readingQueue.put(item);
}

export async function getReadingQueue(): Promise<ReadingQueueItem[]> {
  return db.readingQueue.orderBy('position').toArray();
}

export async function deleteReadingQueueItem(id: string): Promise<void> {
  await db.readingQueue.delete(id);
}

export async function updateReadingQueueItem(id: string, updates: Partial<ReadingQueueItem>): Promise<void> {
  const existing = await db.readingQueue.get(id);
  if (existing) {
    await db.readingQueue.put({ ...existing, ...updates });
  }
}

export async function getReadingQueueByItemId(itemId: string): Promise<ReadingQueueItem | undefined> {
  return db.readingQueue.where('itemId').equals(itemId).first();
}

export async function getNextReadingQueueItem(): Promise<ReadingQueueItem | undefined> {
  const queued = await db.readingQueue.where('status').equals('queued').sortBy('position');
  return queued[0];
}
